/**
 * Reusable modal dialog component.
 * Renders a fixed overlay with a centered card. Closes on overlay
 * click or Escape key press.
 *
 * @module components/Modal
 */

import React, { useEffect, useCallback } from 'react';

/**
 * Renders a modal dialog.
 *
 * @param {object} props
 * @param {string}             props.title    - Modal heading text.
 * @param {React.ReactNode}    props.children - Modal body content.
 * @param {Function}           props.onClose  - Called when the user dismisses the modal.
 * @returns {React.ReactElement}
 */
export default function Modal({ title, children, onClose }) {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /**
   * Close when clicking the overlay (not the card itself).
   *
   * @param {React.MouseEvent} e
   */
  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-card" role="dialog" aria-modal="true">
        <h2 className="modal-title">{title}</h2>
        {children}
      </div>
    </div>
  );
}
